import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, Banknote, ChevronRight } from "lucide-react";
import { Suspense } from "react";

import { orpc } from "@/client";
import { ErrorBoundary } from "@/components/error-boundary";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { FinanceiroLabels } from "@/lib/labels/financeiro";

import { FinanceOverviewSkeleton } from "./area-skeleton";
import { BalancesPanel } from "./balances-panel";
import { MetricCard } from "./primitives";
import { ReceivablesPanel } from "./receivables-panel";
import { ReconciliationTimeline } from "./reconciliation-timeline";
import type { FinanceArea, FinanceModal } from "./types";

interface OverviewAreaProps {
	month: string;
	onArea: (area: FinanceArea) => void;
	onModal: (modal: FinanceModal) => void;
}

export function OverviewArea(props: OverviewAreaProps) {
	return (
		<>
			<BalancesPanel />
			<ReceivablesPanel month={props.month} />
			<ErrorBoundary key={props.month}>
				<Suspense fallback={<FinanceOverviewSkeleton />}>
					<OverviewContent {...props} />
				</Suspense>
			</ErrorBoundary>
		</>
	);
}

function OverviewContent({ month, onArea, onModal }: OverviewAreaProps) {
	const { data } = useSuspenseQuery(orpc.financeiro.overview.queryOptions({ input: { month } }));

	return (
		<>
			<div className="mt-5 grid gap-3 sm:grid-cols-3">
				<MetricCard
					tone="hero"
					label="Resultado no banco"
					value={FinanceiroLabels.signed(data.bank.result_cents)}
					detail={`${data.bank.count} movimentos confirmados`}
				/>
				<MetricCard
					icon={ArrowDownLeft}
					label="Recebimentos confirmados"
					value={FinanceiroLabels.money(data.receipts.confirmed_cents)}
					detail={`${data.receipts.confirmed_count} recebimentos`}
				/>
				<MetricCard
					icon={ArrowUpRight}
					label="Contas abertas"
					value={FinanceiroLabels.money(data.bills.open_cents)}
					detail={`${data.bills.open_count} contas`}
				/>
			</div>

			<section className="mt-5">
				<div className="flex items-center justify-between">
					<div>
						<h2 className="type-section-title text-foreground">Conciliação</h2>
						<p className="text-xs text-muted-foreground">
							{data.reconciliation.total} itens em aberto
						</p>
					</div>
					<Button variant="ghost" size="sm" onClick={() => onArea("reconciliation")}>
						Abrir conciliação
						<ChevronRight />
					</Button>
				</div>

				{data.reconciliation.data.length === 0 && (
					<div className="surface-panel mt-4">
						<EmptyState
							icon={Banknote}
							title="Tudo resolvido neste mês"
							description="Os recebimentos, repasses e movimentos bancários estão conciliados."
						/>
					</div>
				)}

				{data.reconciliation.data.length > 0 && (
					<ReconciliationTimeline items={data.reconciliation.data} onModal={onModal} />
				)}
			</section>
		</>
	);
}
